import { padNumber as pad } from './DateHelpers';

/**
* @param {Number} number
* @returns {string}
*/
function ordinal(number) {
  if (number > 3 && number < 21) return `${number}th`;

  const mod = number % 10;
  if (mod === 1) return `${number}st`;
  if (mod === 2) return `${number}nd`;
  if (mod === 3) return `${number}rd`;
  return `${number}th`;
}


const weekdays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const tokenMap = new Map([
  // This is an *INCOMPLETE* implementation of moment's
  // formatting tokens. Token order in this map is important -
  // the tokens/keys are used to build the regex, so make sure
  // they're most-to-least specific.

  // https://momentjs.com/docs/#/displaying/

  // Month
  ['MMMM', date => months[date.getMonth()]],
  ['MMM', date => months[date.getMonth()].substr(0, 3)],
  ['MM', date => pad(date.getMonth() + 1)],
  ['Mo', date => ordinal(date.getMonth() + 1)],
  ['M', date => date.getMonth() + 1],

  // Quarter
  ['Qo', date => ordinal(Math.floor((date.getMonth() + 3) / 3))],
  ['Q', date => Math.floor((date.getMonth() + 3) / 3)],

  // Day of Month
  ['DD', date => pad(date.getDate())],
  ['Do', date => ordinal(date.getDate())],
  ['D', date => date.getDate()],

  // Day of Week
  ['dddd', date => weekdays[date.getDay()]],
  ['ddd', date => weekdays[date.getDay()].substr(0, 3)],
  ['dd', date => weekdays[date.getDay()].substr(0, 2)],
  ['do', date => ordinal(date.getDay())],
  ['d', date => date.getDay()],

  // Year
  ['YYYY', date => date.getFullYear()],
  ['YY', date => date.getFullYear().toString().slice(2, 4)],

  // AM/PM
  ['A', date => (date.getHours() < 12 ? 'AM' : 'PM')],
  ['a', date => (date.getHours() < 12 ? 'am' : 'pm')],

  // Hour
  ['HH', date => pad(date.getHours())],
  ['H', date => date.getHours()],
  ['hh', date => pad((date.getHours() % 12) + 1)],
  ['h', date => (date.getHours() % 12) + 1],
  ['kk', date => pad(date.getHours() + 1)],
  ['k', date => date.getHours() + 1],

  // Minute
  ['mm', date => pad(date.getMinutes())],
  ['m', date => date.getMinutes()],

  // Second
  ['ss', date => pad(date.getSeconds())],
  ['s', date => date.getSeconds()],

  // Time Zone
  ['Z', (date) => {
    const offset = date.getTimezoneOffset();

    if (offset === 0) {
      return 'Z';
    }

    const plusMinus = offset < 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offset) / 60);
    const minutes = Math.abs(offset) % 60;

    return `${plusMinus}${pad(hours)}:${pad(minutes)}`;
  }],
]);

const regex = new RegExp([
  // Escapes characters surrounded by brackets:
  '\\[([^\\]]+)\\]',

  // Match any token:
  `(${Array.from(tokenMap.keys()).join('|')})`,

  // Match anything else to pass-through
  '.',
].join('|'), 'g');


class ReplacementToken {
  constructor(token, fn) {
    this.token = token;
    this.fn = fn;
  }

  getValue(date) {
    return this.fn(date);
  }
}

class PassThroughString {
  constructor(value) {
    this.value = value;
  }

  append(value) {
    this.value += value;
  }

  getValue() {
    return this.value;
  }
}

/**
 * @param {string} picture
 * @returns {Array.<ReplacementToken|PassThroughString>}
 */
function parsePictureString(picture) {
  const pieces = [];
  let m;

  while ((m = regex.exec(picture)) !== null) {
    // This is necessary to avoid infinite loops with zero-width matches
    if (m.index === regex.lastIndex) {
      regex.lastIndex += 1;
    }

    let stringValue = null;
    const bracketEscapedValue = m[1];

    if (bracketEscapedValue) {
      stringValue = bracketEscapedValue;
    } else {
      const token = m[0];
      const fn = tokenMap.get(token);
      if (fn) {
        pieces.push(new ReplacementToken(token, fn));
      } else {
        stringValue = token;
      }
    }

    if (stringValue !== null) {
      const lastAddedPiece = pieces[pieces.length - 1];
      if (lastAddedPiece instanceof PassThroughString) {
        lastAddedPiece.append(stringValue);
      } else {
        pieces.push(new PassThroughString(stringValue));
      }
    }
  }

  return pieces;
}

class DateFormatPicture {
  /**
   * @param {String} picture
   */
  constructor(picture) {
    /**
     * @type {Array.<ReplacementToken|PassThroughString>}
     */
    this.pieces = parsePictureString(picture);
  }

  /**
   * @param {Date} date
   * @returns {string}
   */
  format(date) {
    return this.pieces.reduce((value, piece) => value + piece.getValue(date), '');
  }
}


/**
 * @type {Map.<string, DateFormatPicture>}
 */
const instanceMap = new Map();

/**
 * @param {string} picture
 * @returns {DateFormatPicture}
 */
function getPictureInstance(picture) {
  let instance = instanceMap.get(picture);

  if (!instance) {
    instance = new DateFormatPicture(picture);
    instanceMap.set(picture, instance);
  }

  return instance;
}

/**
 * @param {Date} date
 * @param {string} picture
 * @returns {string}
 */
function format(date, picture) {
  return getPictureInstance(picture).format(date);
}

/**
 * @param {Date} date
 * @returns {string}
 */
function formatISOBasic(date) {
  return format(date, 'YYYY-MM-DDTHH:mm');
}


export {
  format,
  formatISOBasic,
};
